import { Localized } from 'dg-web-shared/lib/Localization';
import {
    Box,
    Button,
    Fade,
    Stack,
    Step,
    StepIcon,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';
import { RequestStatus } from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { type JSX, ReactNode } from 'react';
export interface WizardStepInfo<Step extends number> {
    step: Step;
    label: ReactNode;
}

export function WizardStepper<Step extends number>({
    steps,
    stepsWithClearance,
    activeStep,
    offset = 0,
    onStepClick,
}: {
    steps: WizardStepInfo<Step>[];
    stepsWithClearance: Step[];
    activeStep: Step;
    offset?: number;
    onStepClick: (step: Step) => void;
}) {
    return (
        <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={theme => ({
                backgroundColor: theme.palette.primary.light,
                paddingTop: theme.spacing(3),
                paddingBottom: theme.spacing(2),
            })}
            nonLinear
        >
            {steps.map((stepInfo, index) => {
                const disabled = !stepsWithClearance.includes(stepInfo.step);
                return (
                    <Step
                        key={stepInfo.step}
                        sx={{
                            cursor: disabled ? 'default' : 'pointer',
                        }}
                        onClick={() => !disabled && onStepClick(stepInfo.step)}
                    >
                        <StepLabel
                            icon={
                                <StepIcon
                                    active={stepInfo.step === activeStep}
                                    icon={index + 1 + offset}
                                />
                            }
                        >
                            <Typography variant="overline">
                                {stepInfo.label}
                            </Typography>
                        </StepLabel>
                    </Step>
                );
            })}
        </Stepper>
    );
}

export function WizardBottomBar({
    onNextClick,
    nextLabel = <Localized de="Weiter" fr="Continuer" it="Avanti" en="Next" />,
    nextAsSubmitRequestState,
    onPreviousClick,
    previousLabel = (
        <Localized de="Zurück" fr="Retour" it="Indietro" en="Back" />
    ),
    hidePrevious = false,
}: {
    onNextClick?: () => void;
    nextLabel?: ReactNode;
    nextAsSubmitRequestState?: RequestStatus;
    onPreviousClick?: () => void;
    previousLabel?: ReactNode;
    hidePrevious?: boolean;
}) {
    return (
        <Stack
            direction="row"
            spacing={0}
            sx={[
                {
                    justifyContent: 'space-between',
                    alignItems: 'stretch',
                },
                theme => ({
                    backgroundColor: theme.palette.primary.light,
                    padding: theme.spacing(1),
                    filter: `drop-shadow(0px -12px 4px rgba(255,255,255,0.74))`,
                }),
            ]}
        >
            <Button
                onClick={onPreviousClick}
                disabled={!onPreviousClick}
                sx={{
                    visibility: hidePrevious ? 'hidden' : undefined,
                }}
            >
                <KeyboardArrowLeft /> {previousLabel}
            </Button>
            {nextAsSubmitRequestState ? (
                <Button
                    loading={nextAsSubmitRequestState === RequestStatus.PENDING}
                    variant="contained"
                    type="submit"
                    disabled={!onNextClick}
                    color="success"
                    sx={{ width: '50%' }}
                    key="success"
                    onClick={onNextClick}
                >
                    {nextLabel}
                </Button>
            ) : (
                <Button onClick={onNextClick} disabled={!onNextClick}>
                    {nextLabel} <KeyboardArrowRight />
                </Button>
            )}
        </Stack>
    );
}

export function WizardProductHeader({ children }: { children: ReactNode }) {
    return (
        <Typography
            variant="h3"
            sx={theme => ({
                padding: theme.spacing(2),
                backgroundColor: theme.palette.grey[500],
                color: theme.palette.primary.contrastText,
                textAlign: 'center',
            })}
        >
            {children}
        </Typography>
    );
}

export function WizardFadeInEffect({
    children,
}: {
    children: ReactNode;
}): JSX.Element {
    return (
        <Fade in={true}>
            <div>{children}</div>
        </Fade>
    );
}

export function WizardBody({ children }: { children: ReactNode }) {
    return (
        <Box
            sx={{
                paddingX: theme => theme.spacing(3),
                paddingY: theme => theme.spacing(2),
                flexGrow: 1,
                overflowY: 'scroll',
                scrollbarWidth: 'none' /* Firefox */,
                msOverflowStyle: 'none' /* Internet Explorer, Edge */,
                '&::-webkit-scrollbar': {
                    display: 'none',
                } /* Chrome */,
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: theme => theme.palette.background.default,
            }}
        >
            {children}
        </Box>
    );
}
